import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RouteComponent, withRouter, type RouteComponentProps } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import noop from 'lodash/noop';

import { apologise as apologiseAction } from 'Redux/thunks/apology.thunks';
import { DatasetActions } from 'Redux/thunks/datasets.thunks';
import { getFromLocalStorage, setIntoLocalStorage } from 'Internals/local-storage';
import { getType } from 'Internals/datasets';
import { getUpstreamPipeFromDatasetId } from 'Internals/pipes';
import { registerKey, unregisterKey } from 'Internals/global-shortcuts';
import { LoadingPanel } from 'Common/LoadingPanel';

import { useLastOffset } from '../../hooks/useLastOffset.hook';
import { useGetInspectorDataset } from '../../hooks/useGetInspectorDataset.hook';
import { useIsLiveUpdatesEnabled } from '../../hooks/useIsLiveUpdatesEnabled.hook';
import { useDatasetInspectorState } from '../../hooks/useDatasetInspector/useDatasetInspectorState';

import { confirmBefore } from '../../utils/common.utils';

import Button from 'Common/Button/Button';
import PostEntityModal from './PostEntityModal';
import { DatasetInspectorContext } from './DatasetInspectorContext';
import DatasetInspectorTable from './dataset-inspector-table/DatasetInspectorTable';
import DatasetInspectorViewer from './dataset-inspector-viewer/DatasetInspectorViewer';
import { DatasetInspectorFilter } from './dataset-inspector-filter/DatasetInspectorFilter';
import { DatasetInspectorBrowser } from './dataset-inspector-browser/DatasetInspectorBrowser.view';

import type { RootState } from '../../types/state.types';
import type { RouterCommonParams } from '../../types/common.types';
import type { Entity, InspectorActions } from '../../types/entities.types';

import './DatasetInspectorStyle.css';

const LOCAL_STORAGE_KEY = 'sesam--entityview-filter';
const TABLE_VIEW_KEY = 'showTableView';

const DatasetInspectorContainer: React.FC<
  RouteComponentProps<RouterCommonParams, any> & {
    tab?: string;
    isPipeExecutionLog?: boolean;
    datasetId?: string;
    targetDatasetId: string;
    subset: string;
    displayRepostAll?: boolean;
    addCustomActions: () => void;
    removeCustomActions: () => void;
    registerRefresh: (func: Function) => void;
    unregisterRefresh: (func: Function) => void;
  }
> = ({
  params,
  router,
  routes,
  subset,
  targetDatasetId,
  datasetId,
  displayRepostAll,
  addCustomActions = noop,
  removeCustomActions = noop,
  registerRefresh = noop,
  unregisterRefresh = noop,
  isPipeExecutionLog = false,
  tab = '',
}) => {
  const currentDatasetId: string = params?.datasetID ?? datasetId;

  const dispatch = useDispatch();

  const { dataset, originalDataset } = useGetInspectorDataset(currentDatasetId);
  const isLiveUpdatesEnabled = useIsLiveUpdatesEnabled(params?.subId);

  const currentDataset = dataset ?? originalDataset;

  const lastOffset = currentDataset?.runtime['last-offset'];
  const { entitiesLiveUpdated, offsetDifference } = useLastOffset(lastOffset);

  const pipes = useSelector((state: RootState) => state.pipes);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);
  const upstreams = useSelector((state: RootState) => state.upstreams);
  const datasetsLoaded = useSelector(
    (state: RootState) => state.loadStatus.datasetsInternalsLoading
  );
  const isDatasetsLoading = useSelector((state: RootState) => state.loadStatus.datasetsLoading);

  const loadDataset = useCallback(() => {
    dispatch(DatasetActions.load(currentDatasetId, true));
  }, [dispatch, currentDatasetId]);

  const apologise = (err: string) => dispatch(apologiseAction(err));

  const rollback = (id: string) => dispatch(DatasetActions.rollback(id));

  const commit = (id: string) => dispatch(DatasetActions.commit(id));

  const {
    filter,
    loadMore,
    loadMoreTop,
    loadNextEntity,
    loadPreviousEntity,
    navigateNext,
    navigatePrevious,
    populate,
    postDeadEntity,
    repostDeadEntities,
    postEntities,
    postJson,
    postReset,
    repost,
    reset,
    resetJump,
    resetPreviousEntity,
    resetSearch,
    search,
    selectEntity,
    setFilter,
    setJumpSequenceValue,
    setJumpType,
    setJumpUpdatedValue,
    setPost,
    setPostValid,
    setSearchType,
    setSearchValue,
    setUncommitted,
    sendPost,
    sourceEntityPath,
    state,
    stopSearch,
    testPreviousEntity,
  } = useDatasetInspectorState({
    apologise,
    dataset,
    datasetID: currentDatasetId,
    datasetsLoaded,
    loadDataset,
    originalDataset,
    params,
    pipes,
    router,
    routes,
    subUrl,
    token,
    upstreams,
    subset,
    targetDatasetId,
  });

  const persistedShowTableView = getFromLocalStorage(
    LOCAL_STORAGE_KEY,
    TABLE_VIEW_KEY,
    false
  ) as boolean;

  const [showTableView, setShowTableView] = useState(persistedShowTableView);

  function toggleTableView() {
    setShowTableView((tw) => !tw);
  }

  useEffect(() => {
    populate();
  }, [currentDatasetId]);

  useEffect(() => {
    /**
     * Keep {state.showTableView} in local storage
     */
    setIntoLocalStorage(LOCAL_STORAGE_KEY, TABLE_VIEW_KEY, showTableView);
  }, [showTableView]);

  const prevScrollTop = useRef(0);

  const itemElement = useRef<HTMLDivElement>(null);
  const currentEl = useRef<HTMLDivElement>(null);
  const listElement = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Register refresh handlers
   */
  useEffect(() => {
    function refresh() {
      populate();
      loadDataset();
    }
    registerRefresh(refresh);

    return () => {
      unregisterRefresh(refresh);
    };
  });

  /**
   * The loading of new entities is done by scrolling down or up in the enities list.
   * {hasMore} & {hasMoreTop} lets us know whether the dataset has more entities
   * to append or prepend to the current list.
   */
  function handleScroll() {
    if (!state.populating) {
      const container = showTableView ? tableContainerRef.current : listElement.current;
      const itemHeight = showTableView ? 32 : 69;

      if (container?.scrollTop) {
        prevScrollTop.current = container?.scrollTop;
      }

      /**
       * If {scrollTop} is within 2 * {itemHeight} from the bottom of the {container} and the dataset
       * has more entities to append, then load more entitieds and append them to the current list of entitites
       */
      if (
        container &&
        container.clientHeight + container.scrollTop >= container.scrollHeight - itemHeight * 2
      ) {
        if (state.hasMore && !state.loadingMore) {
          // only load if we are not already loading
          loadMore();
        }
      }

      /**
       * If {scrollTop} is within 2 * {itemHeight} from the top of the {container} and the dataset
       * has more entitites to prepend, then load more entities and prepend to the current list of entities
       */
      if (container && container.scrollTop < itemHeight * 2) {
        if (state.hasMoreTop && !state.loadingMoreTop) {
          // only load if we are not already loading
          loadMoreTop();
        }
      }
    }
  }

  const upstreamPipeId = useMemo(() => {
    if (!dataset) {
      return undefined;
    }

    const upstreamPipe = getUpstreamPipeFromDatasetId(dataset._id, pipes, upstreams);
    return upstreamPipe ? upstreamPipe['_id'] : undefined;
  }, [dataset, pipes, upstreams]);

  useLayoutEffect(() => {
    if (currentEl && currentEl.current && !state.populating) {
      currentEl.current.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [state.selectedEntity, state.populating]);

  /**
   * Register key handlers for moving up and down the list of entities
   */
  useEffect(() => {
    function next(ev: Event) {
      ev.preventDefault();
      navigateNext();
    }

    function prev(ev: Event) {
      ev.preventDefault();
      navigatePrevious();
    }

    registerKey('j', next);
    registerKey('ArrowDown', next);
    registerKey('k', prev);
    registerKey('ArrowUp', prev);

    return () => {
      unregisterKey('j');
      unregisterKey('ArrowDown');
      unregisterKey('k');
      unregisterKey('ArrowUp');
    };
  }, [navigateNext, navigatePrevious]);

  /**
   * Scroll from top to make sure the selected entity from
   * a jump always stays inside the list client window
   */
  useEffect(() => {
    let containerEl = showTableView ? tableContainerRef.current : listElement.current;
    let itemHeight = showTableView ? 30 : 70;

    if (state.hasMoreTop) {
      if (containerEl && containerEl.clientHeight < containerEl.scrollHeight) {
        if (containerEl.scrollTop < itemHeight) {
          containerEl.scrollTop = prevScrollTop.current + (containerEl.clientHeight - itemHeight);
        }
      }
    }
  }, [state.entities, state.selectedEntity, state.hasMoreTop, showTableView]);

  const rollbackDataset = () => {
    rollback(currentDatasetId);
  };

  const commitDataset = () => {
    commit(currentDatasetId);
  };

  const circuitBreakerHasTripped = dataset && dataset.runtime['circuit-breaker-tripped'];

  const circuitBreakerButtons = (
    <React.Fragment>
      {circuitBreakerHasTripped && (
        <Button
          key="roll-back-dataset"
          onClick={() =>
            confirmBefore('Are you sure you want to roll back this dataset?', rollbackDataset)
          }
        >
          Roll back dataset
        </Button>
      )}
      {circuitBreakerHasTripped && (
        <Button
          key="commit-dataset"
          onClick={() =>
            confirmBefore('Are you sure you want to commit this dataset?', commitDataset)
          }
        >
          Commit dataset
        </Button>
      )}
    </React.Fragment>
  );

  /**
   * Add custom actions to the actions in the top right corner
   */
  useEffect(() => {
    let actions = [
      <Button theme="primary" key="postEntity" onClick={() => postEntities()}>
        Post entity…
      </Button>,
    ];

    if (circuitBreakerHasTripped) {
      actions.push(circuitBreakerButtons);
    }
    addCustomActions(actions);

    return () => {
      removeCustomActions();
    };
  }, [addCustomActions, removeCustomActions, circuitBreakerHasTripped]);

  const handleEntityRef = useCallback(
    (el: HTMLDivElement, entity: Entity, idx: number) => {
      if (state.selectedEntity === entity) {
        currentEl.current = el;
      }
      if (idx === 0) {
        itemElement.current = el;
      }
    },
    [state.selectedEntity]
  );

  const actions: InspectorActions = {
    loadNextEntity,
    loadPreviousEntity,
    postDeadEntity,
    repost,
    resetJump,
    reset,
    resetPreviousEntity,
    resetSearch,
    search,
    selectEntity,
    sendPost,
    setFilter,
    setJumpSequenceValue,
    setJumpType,
    setJumpUpdatedValue,
    setSearchType,
    setSearchValue,
    setUncommitted,
    testPreviousEntity,
    toggleTableView,
  };

  if (isDatasetsLoading) {
    return <LoadingPanel />;
  }

  if (!currentDataset) {
    return null;
  }

  return (
    <DatasetInspectorContext.Provider
      value={{
        state,
        actions,
        showTableView,
        currentDatasetId,
        entitiesLiveUpdated,
        isLiveUpdatesEnabled,
      }}
    >
      <div className="dataset-inspector">
        <div className="dataset-inspector__filter">
          <DatasetInspectorFilter
            dataset={dataset}
            filter={filter}
            subset={subset}
            entities={state.entities}
            pipes={pipes}
          />
        </div>
        {showTableView ? (
          <DatasetInspectorTable
            onScroll={handleScroll}
            onSetTableContainerRef={(el: HTMLDivElement) => {
              tableContainerRef.current = el;
            }}
            showingSearchResults={state.showingFreetextResults || state.showingIdResults}
            upstreamPipeId={upstreamPipeId}
            dataset={dataset}
            filter={filter}
          />
        ) : (
          <div className="dataset-inspector__browser">
            <DatasetInspectorBrowser
              onScroll={handleScroll}
              onSetRef={(el: HTMLDivElement) => {
                listElement.current = el;
              }}
              onEntityRef={handleEntityRef}
              showingSearchResults={state.showingFreetextResults || state.showingIdResults}
              dataset={dataset}
              filter={filter}
              stopSearch={stopSearch}
              displayRepostAll={displayRepostAll}
              repostDeadEntities={repostDeadEntities}
              pipeId={params.pipeID}
              populate={populate}
              offsetDifference={offsetDifference}
            />
            <DatasetInspectorViewer
              sourceEntityPath={sourceEntityPath}
              isDeadLetterDataset={dataset && getType(dataset._id) === 'dead_letter_dataset'}
              filter={filter}
              isPipeExecutionLog={isPipeExecutionLog}
              tab={tab}
              displayRepostAll={displayRepostAll}
              repostDeadEntities={repostDeadEntities}
            />
          </div>
        )}
        <PostEntityModal
          onClose={() => postReset()}
          onPostChange={setPost}
          onPostValidation={setPostValid}
          rawJson={postJson}
          sendPost={sendPost}
          showPost={state.showPost}
          postMultiple={state.postMultiple}
          isPostDisabled={state.isPostDisabled}
          postValidationError={state.postValidationError}
          shouldForceIfDuplicate={displayRepostAll ? true : false}
        />
      </div>
    </DatasetInspectorContext.Provider>
  );
};

const Routed = withRouter(DatasetInspectorContainer as unknown as RouteComponent);

export { Routed as DatasetInspectorContainer };
